import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchFilterProjects from '../components/SearchFilterProjects';
import { searchProjects } from '../services/database';
import { FolderPlus, Building2 } from 'lucide-react';
import { isEnterpriseUser } from '../services/enterpriseSharing';

const AllProjects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('dateDesc');
  const [isSearching, setIsSearching] = useState(false);
  const [isEnterprise, setIsEnterprise] = useState(false); // Restored enterprise state
  const debounceTimerRef = useRef(null);
  const navigate = useNavigate();

  // Initial data load
  useEffect(() => {
    const loadInitialProjects = async () => {
      try {
        const data = await searchProjects('', 'dateDesc');
        setProjects(data);
        setFilteredProjects(data);
        setHasLoadedInitial(true);
      } catch (error) {
        console.error('Error loading initial projects:', error);
      }
    };

    loadInitialProjects();
  }, []);

  // Check enterprise status - Restored
  useEffect(() => {
    const checkEnterpriseStatus = async () => {
      try {
        const enterpriseStatus = await isEnterpriseUser();
        setIsEnterprise(enterpriseStatus);
      } catch (error) {
        console.error('Error checking enterprise status:', error);
        setIsEnterprise(false);
      }
    };

    checkEnterpriseStatus();
  }, []);


  // Handle search input changes with debounce
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (term.trim() === '') {
      setFilteredProjects(projects);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchProjects(term, sortOption);
        setFilteredProjects(results);
      } catch (error) {
        console.error('Error searching projects:', error);
      } finally {
        setIsSearching(false);
      }
    }, 400);
  }, [projects, sortOption]);

  // Handle sort option changes
  const handleSort = useCallback(async (option) => {
    setSortOption(option);
    setIsSearching(true);
    try {
      const results = await searchProjects(searchTerm, option);
      setFilteredProjects(results);
    } catch (error) {
      console.error('Error sorting projects:', error);
    } finally {
      setIsSearching(false);
    }
  }, [searchTerm]);

  // Function to handle clearing search from the "No results" message
  const handleClearSearchResults = () => {
    setSearchTerm('');
    setFilteredProjects(projects);
  };

  const handleProjectClick = (projectId) => {
    navigate('/project-details', { state: { projectId } });
  };
  
  // Click handler for Shared With Me button - Restored
  const handleSharedWithMeClick = () => {
    navigate('/shared-with-me');
  };

  // Show initial loading state only on first load
  if (!hasLoadedInitial) {
    return (
      <div className="min-h-screen bg-white font-sans antialiased">
        <Navbar sourcePage="projects" />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  // Show welcome screen ONLY if there are truly no projects
  if (hasLoadedInitial && projects.length === 0) {
    return (
      <div className="min-h-screen font-sans antialiased">
        <Navbar sourcePage="projects" />
        <div className="px-4 md:px-8 lg:px-16 py-12 max-w-4xl mx-auto">
          <div className="text-left mb-12">
            <h1 className="text-4xl font-bold mb-6">
              Welcome to Your Design Journey
            </h1>
            <p className="text-xl text-gray-700 mb-4">
              Transform your creative ideas into stunning prototypes with our 
              AI-powered design tool. We're here to help you bring your vision to life.
            </p>
            <p className="text-xl text-gray-700">
              Get started by creating your first project. Our intuitive interface 
              will guide you through the process, making prototyping faster and 
              more enjoyable than ever.
            </p>
          </div>
{/* Welcome screen buttons - Fixed dimensions and mobile responsive */}
<div className="flex flex-col sm:flex-row justify-left gap-4">
  <button
    onClick={() => navigate('/new-project')}
    className="w-full sm:w-48 p-6 sm:p-8 bg-black text-white rounded-lg hover:bg-gray-800 
      transition-colors duration-200 flex flex-col items-center min-h-[120px] sm:min-h-[140px]"
  >
    <FolderPlus size={40} className="sm:size-[48px] mb-2" />
    <span className="text-base sm:text-lg whitespace-nowrap">New Project</span>
  </button>
  {isEnterprise && (
    <button
      onClick={handleSharedWithMeClick}
      className="w-full sm:w-48 p-6 sm:p-8 bg-black text-white rounded-lg hover:bg-gray-800 
        transition-colors duration-200 flex flex-col items-center min-h-[120px] sm:min-h-[140px]"
    >
      <Building2 size={40} className="sm:size-[48px] mb-2" />
      <span className="text-base sm:text-lg whitespace-nowrap">Shared With You</span>
    </button>
  )}
</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans antialiased">
      <Navbar sourcePage="projects" />
      <div className="px-4 md:px-8 lg:px-16 py-12 max-w-7xl mx-auto">
        {/* Header section - Mobile Responsive Fix */}
        <div className="mb-4">
          {/* Desktop layout - unchanged */}
          <div className="hidden md:flex md:items-center md:justify-between">
            <h1 className="text-3xl font-bold text-black">My Projects</h1>
            <div className="flex items-center space-x-3">
              {isEnterprise && (
                <button
                  onClick={handleSharedWithMeClick}
                  className="text-white bg-black px-4 py-2 rounded-md hover:bg-gray-800 
                    transition-colors duration-200 flex items-center gap-2 relative"
                >
                  <Building2 className="w-5 h-5" />
                  <span>Shared With You</span>
                </button>
              )}
              <button
                onClick={() => navigate('/new-project')}
                className="text-white bg-black px-4 py-2 rounded-md hover:bg-gray-800 transition-colors duration-200 flex items-center gap-2"
              >
                <FolderPlus className="w-5 h-5" /> 
                <span>New Project</span>
              </button> 
            </div>
          </div>
          
          {/* Mobile layout - stacked vertically */}
          <div className="block md:hidden">
            <h1 className="text-3xl font-bold text-black mb-4">My Projects</h1>
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => navigate('/new-project')}
                className="text-white bg-black px-4 py-2 rounded-md hover:bg-gray-800 transition-colors duration-200 flex items-center gap-2 justify-center"
              >
                <FolderPlus className="w-5 h-5" /> 
                <span>New Project</span>
              </button>
              {isEnterprise && (
                <button
                  onClick={handleSharedWithMeClick}
                  className="text-white bg-black px-4 py-2 rounded-md hover:bg-gray-800 
                    transition-colors duration-200 flex items-center gap-2 justify-center relative"
                >
                  <Building2 className="w-5 h-5" />
                  <span>Shared With You</span>
                </button>
              )}
            </div>
          </div>
        </div>
        <hr className="mt-8 mb-8 border-gray-300" />  
        
        <SearchFilterProjects 
          onSearch={handleSearch} 
          onSort={handleSort}
          searchValue={searchTerm}
        />
        
        <div className="min-h-[400px]">
          {isSearching ? (
            <div className="flex items-center justify-center h-full pt-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {filteredProjects.map((project) => (
                <div
                  key={project._id}
                  className="flex flex-col border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 bg-white h-[320px]"
                >
                  <div className="flex flex-col h-full p-6">
                    <div className="mb-4">
                      <h2 className="text-xl font-semibold text-black h-14 overflow-hidden">
                        {project.title}
                      </h2>
                    </div>
                    <div 
                      className="text-gray-600 mb-4 flex-grow"
                      style={{
                        position: 'relative',
                        maxHeight: '4.5em',
                        lineHeight: '1.5em',
                        overflow: 'hidden'
                      }}
                    >
                      <p
                        title={project.description}
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                          wordWrap: 'break-word',
                          overflow: 'hidden',
                          maxHeight: '4.5em',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {project.description}
                      </p>
                      <div 
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '3em',
                          height: '1.5em',
                          background: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 50%)'
                        }}
                      />
                    </div>
                    <div className="mt-auto">
                      <p className="text-sm text-gray-500 mb-4">
                        {new Date(project.createdAt).toLocaleDateString()}
                      </p>
                      <button
                        onClick={() => handleProjectClick(project._id)}
                        className="w-full bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors duration-200 font-medium"
                      >
                        View Project
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            searchTerm && (
              <div className="text-center py-8 bg-gray-50 rounded-lg mt-4">
                <p className="text-gray-600">No projects match your search criteria.</p>
                <button 
                  onClick={handleClearSearchResults}
                  className="mt-2 text-black hover:underline"
                >
                  Clear search
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AllProjects;